import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { SEGMENTS } from "@/data/segmentsData";

const MotionLink = motion.create(Link);
import { ArrowRight } from "lucide-react";
import { Camera, KeyRound, Cable, BellRing, Lock, ShieldAlert, Speaker, Tv, Wifi, Sparkles } from "lucide-react";

const SERVICES = [
  {
    icon: Camera,
    slug: "security-camera-installation",
    title: "CCTV & Security Cameras Installation",
    desc: "HD & 4K camera systems with remote viewing, night vision, and smart motion alerts — professionally wired and configured for your property.",
    descShort: "HD & 4K cameras with remote viewing.",
    img: "/assets/service-cctv.webp",
  },
  {
    icon: KeyRound,
    slug: "access-control",
    title: "Access Control Systems",
    desc: "Keycard, fob, and keypad entry systems for offices, multi-unit buildings, and secure areas.",
    descShort: "Keycard, fob & keypad entry systems.",
    img: "/assets/service-access.webp",
  },
  {
    icon: BellRing,
    slug: "video-doorbell-intercom",
    title: "Intercom & Video Doorbell Installation",
    desc: "See and speak to visitors from anywhere with smart intercoms and video doorbells.",
    descShort: "See & speak to visitors from anywhere.",
    img: "/assets/service-doorbell.webp",
  },
  {
    icon: Cable,
    slug: "structured-cabling",
    title: "Structured Cabling",
    desc: "Clean, organized Cat6/fibre cabling for networks, cameras, and AV — labelled and certified.",
    descShort: "Clean, certified Cat6 & fibre cabling.",
    img: "https://images.unsplash.com/photo-1683322499436-f4383dd59f5a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxuZXR3b3JrJTIwc2VydmVyJTIwY2FibGVzJTIwYmx1ZXxlbnwwfHx8fDE3ODY2NzE2Mzh8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    icon: Lock,
    slug: "smart-locks",
    title: "Smart Light Switches & Smart Lock Installation",
    desc: "Keyless entry with codes, fingerprints, and phone control — never hide a key again.",
    descShort: "Keyless entry with codes & phone control.",
    img: "/assets/service-smartlock.webp",
    imgPos: "right center",
  },
  {
    icon: ShieldAlert,
    slug: "alarm-ulc-monitoring",
    title: "Alarm & ULC Monitoring",
    desc: "Professionally installed alarm systems with ULC-certified 24/7 monitoring for total peace of mind.",
    descShort: "ULC-certified 24/7 alarm monitoring.",
    img: "/assets/service-alarm.webp",
  },
  {
    icon: Speaker,
    slug: "audio-visual-home-theatre",
    title: "Home Theatre & Audio/Visual Systems",
    desc: "Home theatres, whole-home audio, and commercial AV — designed, wired, and tuned by our team.",
    descShort: "Home theatres & whole-home audio.",
    img: "/assets/service-theatre.webp",
  },
  {
    icon: Tv,
    slug: "tv-mounting",
    title: "TV Mounting",
    desc: "Secure, level mounting with concealed cables on any wall type.",
    descShort: "Secure mounting with concealed cables.",
    img: "https://images.unsplash.com/photo-1586024486164-ce9b3d87e09f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHx3YWxsJTIwbW91bnRlZCUyMHR2fGVufDB8fHx8MTc4NjY2MDQzM3ww&ixlib=rb-4.1.0&q=85",
  },
  {
    icon: Wifi,
    slug: "wifi-access-points",
    title: "WiFi Access Points",
    desc: "Blanket your property in fast, reliable WiFi — no more dead zones.",
    descShort: "Fast, reliable WiFi — no dead zones.",
    img: "/assets/service-wifi.webp",
  },
  {
    slug: "holiday-permanent-lighting",
    icon: Sparkles,
    title: "Holiday & Permanent Lighting",
    desc: "Permanent roofline lighting and seasonal holiday displays — installed once, controlled from your phone all year.",
    descShort: "Year-round roofline & holiday lighting.",
    img: "/assets/lighting-rgb.webp",
  },
];

export const Services = () => {
  const reduceMotion = useReducedMotion();
  const [isMobile] = useState(() => window.matchMedia("(max-width: 767px)").matches);
  const columnsPerRow = isMobile ? 2 : 3;
  return (
    <section id="services" data-testid="services-section" className="py-24 lg:py-32 overflow-x-clip">
      <div className="container-x">
        <div
          data-testid="segment-strip"
          className="mb-14 flex flex-wrap items-center justify-between gap-x-8 gap-y-3 border border-zinc-200 rounded-md bg-white px-6 py-5 shadow-sm"
        >
          <nav className="flex flex-wrap items-center gap-x-4 gap-y-2" aria-label="Customer segments">
            {SEGMENTS.map((seg, i) => (
              <span key={seg.slug} className="flex items-center gap-4">
                {i > 0 && <span className="text-[#D4AF37] font-bold">|</span>}
                <Link
                  to={`/segments/${seg.slug}/`}
                  data-testid={`segment-link-${seg.slug}`}
                  className="text-sm font-bold uppercase tracking-[0.15em] text-zinc-800 hover:text-brand-primary transition-colors"
                >
                  {seg.name}
                </Link>
              </span>
            ))}
          </nav>
          <p className="text-sm text-zinc-500">Solutions designed for every environment.</p>
        </div>
        <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-semibold text-brand-primary mb-4">
          Our Services
        </p>
        <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
          <motion.div
            initial={{ y: 24 }}
            whileInView={{ y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter leading-none text-zinc-900 max-w-xl">
              Everything your property needs, under one roof
            </h2>
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              className="origin-left mt-5 h-1 w-20 rounded-full bg-gradient-to-r from-[#8C6D1F] via-[#D4AF37] to-[#E9C767]"
            />
          </motion.div>
          <p className="text-base text-zinc-600 max-w-md leading-relaxed">
            One call handles it all — security, cabling, AV, and networking. No juggling multiple contractors.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3.5 md:gap-6 lg:grid-cols-3 lg:gap-8 overflow-x-clip">
          {SERVICES.map((service, i) => (
            <MotionLink
              key={service.title}
              to={`/services/${service.slug}/`}
              data-testid={`service-card-${service.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              initial={reduceMotion ? false : { opacity: 0, scale: 0.85 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{
                duration: 0.5,
                ease: "easeOut",
                delay: (i % columnsPerRow) * 0.07,
              }}
              className="group flex flex-col bg-white border border-zinc-200 rounded-md overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:border-[#D4AF37]/60 hover:shadow-[0_8px_30px_rgba(212,175,55,0.18)]"
            >
              <div className="relative h-28 sm:h-36 md:h-44 overflow-hidden shrink-0">
                <img
                  src={service.img}
                  alt={service.title}
                  loading="lazy"
                  style={{ objectPosition: service.imgPos || "center" }}
                  className="w-full h-full object-cover transition-transform duration-300 ease-out group-hover:scale-105"
                />
                <span className="absolute bottom-2.5 left-1/2 -translate-x-1/2 md:left-6 md:translate-x-0 md:bottom-3 w-10 h-10 md:w-11 md:h-11 rounded-md bg-brand-primary text-black flex items-center justify-center shadow-lg">
                  <service.icon className="w-5 h-5" />
                </span>
              </div>
              <div className="flex flex-col flex-1 p-3 md:p-6 md:pt-4 text-center md:text-left">
                <h3 className="text-base sm:text-lg md:text-xl font-bold tracking-tight text-zinc-900 leading-snug">{service.title}</h3>
                <p className="mt-1.5 text-sm text-zinc-600 leading-snug md:hidden">{service.descShort}</p>
                <p className="mt-2 text-sm text-zinc-600 leading-relaxed hidden md:block">{service.desc}</p>
                <span
                  data-testid={`service-link-${service.slug}`}
                  className="mt-auto pt-3 md:pt-4 inline-flex items-center justify-center md:justify-start gap-1.5 text-sm font-semibold text-brand-primary group-hover:gap-2.5 transition-all"
                >
                  Learn more <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </MotionLink>
          ))}
        </div>
      </div>
    </section>
  );
};
